import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Form,
  Modal,
  Badge,
  InputGroup,
  FormControl,
  Spinner
} from 'react-bootstrap';
import {
  Plus,
  Trash,
  Pencil,
  Eye,
  Search,
  FileEarmarkSpreadsheet
} from 'react-bootstrap-icons';
import Select from 'react-select';
import axios from '../../api/axios';
import { toast } from "react-toastify";
import Header from '../../layouts/Header';
import { useSelector } from 'react-redux';

const RawMaterials = () => {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);
  const accessToken = localStorage.getItem("authToken");
  const { access } = useSelector((state) => state.login);
  const { email } = useSelector((state) => state.login);
  const [materialAccess, setMaterialAccess] = useState([]);

  useEffect(() => {
    if (Array.isArray(access)) {
      const filteredAccess = access?.filter((perm) =>
        perm?.startsWith("RawMaterials:")
      );
      setMaterialAccess(filteredAccess);
    }
  }, [access]);

  // State for UI
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isPageChanging, setIsPageChanging] = useState(false);

  // State for data
  const [rawMaterials, setRawMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);
  const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);

  // State for forms
  const [formData, setFormData] = useState({
    mainCategory: "",
    subCategory: "",
    name: "",
    reOrderLevel: "",
    description: "",
    unit: "",
    hsn: "",
    taxPercent: "",
    purchaseUnit: "",
  });

  const [editFormData, setEditFormData] = useState({
    _id: "",
    mainCategory: "",
    subCategory: "",
    name: "",
    reOrderLevel: "",
    description: "",
    unit: "",
    hsn: "",
    taxPercent: "",
    purchaseUnit: "",
  });

  // State for filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedMainCategory, setSelectedMainCategory] = useState(null);
  const [selectedMainCategoryData, setSelectedMainCategoryData] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedSubCategoryData, setSelectedSubCategoryData] = useState(null);

  // Format options for react-select
  const mainCategoryOptions = mainCategories.map(cat => ({
    value: cat._id,
    label: cat.name
  }));

  const subCategoryOptions = filteredSubCategories.map(cat => ({
    value: cat._id,
    label: cat.name
  }));

  // API Calls
  const getMainCategories = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get("masters/rm-main-category/get", { headers });

      if (response?.data?.status === true) {
        setMainCategories(response.data.data.categories);
      }
    } catch (error) {
      handleApiError(error, "Failed to fetch main categories");
    }
  };

  const getSubCategories = async (mainCategoryId = null) => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get("masters/rm-sub-category/get", { headers });

      if (response?.data?.status === true) {
        const allSubCategories = response.data.data.categories;
        setSubCategories(allSubCategories);

        if (mainCategoryId) {
          const filtered = allSubCategories.filter(sub => sub.mainCategory === mainCategoryId);
          setFilteredSubCategories(filtered);
        }
      }
    } catch (error) {
      handleApiError(error, "Failed to fetch sub categories");
    }
  };

  const handleDownloadExcel = async () => {
    setDownloadBtnLoading(true);
    getRawMaterials(selectedMainCategory, selectedSubCategory, currentPage, itemsPerPage, true);
  };

  const getRawMaterials = useCallback(async (mainCategoryId, subCategoryId, page, limit, download, search = null) => {
    try {
      setIsLoading(true);
      const headers = { Authorization: `Bearer ${accessToken}` };
      let url = `raw-material`;

      // Collect query params in an object
      const params = {};
      if (mainCategoryId) params.mainCategory = mainCategoryId;
      if (subCategoryId) params.subCategory = subCategoryId;
      if (download) params.isDownload = download;
      if (page) params.page = page;
      if (limit) params.limit = limit;
      if (search) params.search = search;

      const response = await axios.get(url, {
        headers,
        responseType: download ? 'blob' : 'json',
        params,
      });

      if (download) {
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        
        if (!selectedMainCategoryData && !selectedSubCategoryData) {
          link.download = `Raw Materials List.xlsx`;
        } else if (selectedMainCategoryData && selectedSubCategoryData) {
          link.download = `Raw Materials List For ${selectedMainCategoryData} And Sub Category ${selectedSubCategoryData} .xlsx`;
        } else if (selectedMainCategoryData) {
          link.download = `Raw Materials List For ${selectedMainCategoryData}.xlsx`;
        } else if (selectedSubCategoryData) {
          link.download = `Raw Materials List For ${selectedSubCategoryData} .xlsx`;
        }
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);

        toast.success('Excel file downloaded successfully!', {
          position: "top-right",
          autoClose: 3000,
          theme: "light"
        });
      }

      if (response?.data?.status === true) {
        let materials = response.data.data.rawMaterials;

        // Add serial numbers
        materials = materials.map((item, index) => ({
          ...item,
          serialNumber: index + 1
        }));

        setRawMaterials(materials);
        setFilteredMaterials(materials);
        setTotalRecords(response?.data?.pagination?.totalRecords);
        setTotalPages(response?.data?.pagination?.totalPages);
      }
    } catch (error) {
      handleApiError(error, "Failed to fetch raw materials");
    } finally {
      setIsLoading(false);
      setDownloadBtnLoading(false);
      setIsPageChanging(false);
    }
  }, [accessToken, selectedMainCategoryData, selectedSubCategoryData]);

  const getMaterialDetails = async (id) => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get(`raw-material?_id=${id}`, { headers });

      if (response?.data?.status === true) {
        return response.data.data.rawMaterials;
      }
    } catch (error) {
      handleApiError(error, "Failed to fetch material details");
    }
    return null;
  };

  const addMaterial = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const headers = { Authorization: `Bearer ${accessToken}` };
      const data = { ...formData };

      if (!data.reOrderLevel) delete data.reOrderLevel;
      if (!data.purchaseUnit) delete data.purchaseUnit;

      const response = await axios.post("raw-material", data, { headers });

      if (response?.data?.status === true) {
        toast.success(response.data.data.message);
        getRawMaterials(selectedMainCategory, selectedSubCategory, currentPage, itemsPerPage, false, searchTerm);
        resetForm();
        setShowAddModal(false);
      }
    } catch (error) {
      handleApiError(error, "Failed to add material");
    } finally {
      setIsLoading(false);
    }
  };

  const updateMaterial = async (e) => {
    e.preventDefault();
    try {
      setIsEditLoading(true);
      const headers = { Authorization: `Bearer ${accessToken}` };
      const data = { ...editFormData };

      if (!data.reOrderLevel) data.reOrderLevel = null;

      const response = await axios.put("raw-material", data, { headers });

      if (response?.data?.status === true) {
        toast.success(response.data.data.message);
        getRawMaterials(selectedMainCategory, selectedSubCategory, currentPage, itemsPerPage, false, searchTerm);
        setShowEditModal(false);
      }
    } catch (error) {
      handleApiError(error, "Failed to update material");
    } finally {
      setIsEditLoading(false);
    }
  };

  const deleteMaterial = async () => {
    try {
      setIsDeleteLoading(true);
      const headers = { Authorization: `Bearer ${accessToken}` };
      const data = { _id: editFormData._id };

      const response = await axios.delete("raw-material", { data, headers });

      if (response?.data?.status === true) {
        toast.success(response.data.data.message);
        getRawMaterials(selectedMainCategory, selectedSubCategory, currentPage, itemsPerPage, false, searchTerm);
        setShowDeleteModal(false);
      }
    } catch (error) {
      handleApiError(error, "Failed to delete material");
    } finally {
      setIsDeleteLoading(false);
    }
  };

  // Helper functions
  const handleApiError = (error, defaultMessage) => {
    let errorMessage = defaultMessage;

    if (error?.response) {
      if (error.response.status === 400) {
        errorMessage = error.response.data.message || error.response.data.title;
      } else if (error.response.status === 404) {
        errorMessage = error.response.data.message || "Resource not found";
      } else if (error.response.status === 406) {
        errorMessage = error.response.data.error?.message || error.response.data.error;
      } else if (error.response.status === 412) {
        errorMessage = error.response.data.data?.message || error.response.data.error;
      } else if (error.response.status === 500) {
        errorMessage = error.response.data.error || "Server error";
      }
    }

    toast.error(errorMessage);
  };

  const resetForm = () => {
    setFormData({
      mainCategory: "",
      subCategory: "",
      name: "",
      reOrderLevel: "",
      description: "",
      unit: "",
      hsn: "",
      taxPercent: "",
      purchaseUnit: ""
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOption, field) => {
    setFormData(prev => ({ ...prev, [field]: selectedOption?.value || "" }));

    if (field === 'mainCategory') {
      getSubCategories(selectedOption?.value);
      setFormData(prev => ({ ...prev, subCategory: "" }));
    }
  };

  const handleEditSelectChange = (selectedOption, field) => {
    setEditFormData(prev => ({ ...prev, [field]: selectedOption?.value || "" }));

    if (field === 'mainCategory') {
      getSubCategories(selectedOption?.value);
    }
  };

  const handleMainCategoryFilter = (selectedOption) => {
    setSelectedMainCategoryData(selectedOption?.label);
    setSelectedMainCategory(selectedOption?.value || null);
    setSelectedSubCategory(null);
    setCurrentPage(1);
    
    getRawMaterials(selectedOption?.value, null, 1, itemsPerPage, false, searchTerm);

    if (selectedOption?.value) {
      getSubCategories(selectedOption.value);
    }
  };

  const handleSubCategoryFilter = (selectedOption) => {
    setSelectedSubCategoryData(selectedOption?.label);
    setSelectedSubCategory(selectedOption?.value || null);
    setCurrentPage(1);
    getRawMaterials(selectedMainCategory, selectedOption?.value, 1, itemsPerPage, false, searchTerm);
  };

  const handleViewMaterial = async (material) => {
    const details = await getMaterialDetails(material._id);
    if (details) {
      setEditFormData({
        _id: details._id,
        mainCategory: details.mainCategory?._id || "",
        subCategory: details.subCategory?._id || "",
        name: details.name || "",
        description: details.description || "",
        unit: details.unit || "",
        hsn: details.hsn || "",
        taxPercent: details.taxPercent || 0,
        reOrderLevel: details.reOrderLevel || "",
      });
      setShowViewModal(true);
    }
  };

  const handleEditMaterial = async (material) => {
    const details = await getMaterialDetails(material._id);
    if (details) {
      setEditFormData({
        _id: details._id,
        mainCategory: details.mainCategory?._id || "",
        subCategory: details.subCategory?._id || "",
        name: details.name || "",
        description: details.description || "",
        unit: details.unit || "",
        hsn: details.hsn || "",
        taxPercent: details.taxPercent || 0,
        reOrderLevel: details.reOrderLevel || "",
        purchaseUnit: details.purchaseUnit || null
      });

      if (details.mainCategory?._id) {
        getSubCategories(details.mainCategory._id);
      }

      setShowEditModal(true);
    }
  };

  const handleDeleteMaterial = (material) => {
    setEditFormData({
      _id: material._id,
      name: material.name
    });
    setShowDeleteModal(true);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;

    setIsPageChanging(true);
    setCurrentPage(page);
    
    getRawMaterials(
      selectedMainCategory,
      selectedSubCategory,
      page,
      itemsPerPage,
      false,
      searchTerm
    );
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = Number(e.target.value);
    setItemsPerPage(newLimit);
    setCurrentPage(1);

    getRawMaterials(
      selectedMainCategory,
      selectedSubCategory,
      1,
      newLimit,
      false,
      searchTerm
    );
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Initialize data
  useEffect(() => {
    getMainCategories();
    getSubCategories();
    getRawMaterials(null, null, currentPage, itemsPerPage);
  }, []);

  // Debounced search effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      getRawMaterials(
        selectedMainCategory,
        selectedSubCategory,
        1,
        itemsPerPage,
        false,
        searchTerm || null
      );
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, getRawMaterials]);

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className={`main main-app p-3 p-lg-4 ${skin === 'dark' ? 'bg-dark text-white' : 'bg-light'}`}>
        <Container className='bg-white' fluid>
          {/* Header and Filters */}
          <Row className="mb-4 pt-2">
            <Col className='d-flex align-items-center'>
              <h4 className="mb-0 fw-semibold">RAW MATERIALS</h4>
              <span className="text-muted ms-2">(Manage your raw materials inventory).</span>
            </Col>
            <Col className="d-flex justify-content-end">
              {(materialAccess?.includes("RawMaterials:add") || email === 'contact@xpansecafe.com') && (
                <Button
                  variant="primary"
                  onClick={() => setShowAddModal(true)}
                  className="d-flex align-items-center text-center fw-semibold w-25"
                >
                  <Plus size={18} className="me-2" /> Add New
                </Button>
              )}
            </Col>
            <hr className='mt-2 mb-0' />
          </Row>

          <Row className="mb-4">
            <Col md={3}>
              <InputGroup>
                <InputGroup.Text>
                  <Search />
                </InputGroup.Text>
                <FormControl
                  placeholder="Search materials..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </InputGroup>
            </Col>
            <Col md={6} className="d-flex justify-content-end">
              <div className="me-2" style={{ width: '260px' }}>
                <Select
                  options={mainCategoryOptions}
                  value={mainCategoryOptions.find(opt => opt.value === selectedMainCategory)}
                  onChange={handleMainCategoryFilter}
                  placeholder="Filter by Main Category"
                  isClearable
                />
              </div>
              <div style={{ width: '260px' }}>
                <Select
                  options={subCategoryOptions}
                  value={subCategoryOptions.find(opt => opt.value === selectedSubCategory)}
                  onChange={handleSubCategoryFilter}
                  placeholder="Filter by Sub Category"
                  isClearable
                  isDisabled={!selectedMainCategory}
                />
              </div>
            </Col>

            <Col md={3}>
              <Button
                variant="outline-success"
                className="w-100 fw-semibold"
                disabled={downloadBtnLoading}
                onClick={handleDownloadExcel}
              >
                <FileEarmarkSpreadsheet className=" me-2" />
                {downloadBtnLoading ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  "Download Excel"
                )}
              </Button>
            </Col>
          </Row>

          {/* Materials Table */}
          <Row>
            <Col>
              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : (
                <>
                  {(materialAccess?.includes("RawMaterials:get") || email === 'contact@xpansecafe.com') && (
                    <div className="table-responsive">
                      <Table striped bordered hover variant={skin === 'dark' ? 'dark' : ''}>
                        <thead className='bg-dark text-white'>
                          <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Main Category</th>
                            <th>Sub Category</th>
                            <th>HSN</th>
                            <th>Tax %</th>
                            <th>Unit</th>
                            <th>Re-Order Level</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredMaterials?.length > 0 ? (
                            filteredMaterials?.map((material, index) => (
                              <tr key={material._id}>
                                <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                <td>{material.name}</td>
                                <td>{material.mainCategory?.name || '-'}</td>
                                <td>{material.subCategory?.name || '-'}</td>
                                <td>{material.hsn || '-'}</td>
                                <td>{material.taxPercent ? `${material.taxPercent}%` : '0%'}</td>
                                <td>
                                  <Badge bg="secondary">
                                    {material.unit || '-'}
                                  </Badge>
                                </td>
                                <td>{material.reOrderLevel || '-'}</td>
                                <td>
                                  <div className="d-flex">
                                    {(materialAccess?.includes("RawMaterials:get") || email === 'contact@xpansecafe.com') && (
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="me-2"
                                        onClick={() => handleViewMaterial(material)}
                                      >
                                        <Eye />
                                      </Button>
                                    )}
                                    {(materialAccess?.includes("RawMaterials:update") || email === 'contact@xpansecafe.com') && (
                                      <Button
                                        variant="outline-success"
                                        size="sm"
                                        className="me-2"
                                        onClick={() => handleEditMaterial(material)}
                                      >
                                        <Pencil />
                                      </Button>
                                    )}
                                    {(materialAccess?.includes("RawMaterials:delete") || email === 'contact@xpansecafe.com') && (
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleDeleteMaterial(material)}
                                      >
                                        <Trash />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="9" className="text-center">
                                {searchTerm ? 'No materials match your search' : 'No raw materials found'}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}

                  {/* Pagination */}
                  {rawMaterials.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center mt-1 pb-2 flex-wrap gap-2">
                      {/* Left: Showing info */}
                      <div>
                        Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords} entries
                      </div>

                      {/* Center: Records per page */}
                      <div className="d-flex align-items-center gap-2">
                        <span>Show</span>
                        <select
                          className="form-select form-select-sm w-auto"
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                          disabled={isPageChanging}
                        >
                          {[5, 10, 20, 50, 100].map(size => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                        <span>entries</span>
                      </div>

                      {/* Right: Pagination buttons */}
                      {totalPages > 1 && (
                        <div className="d-flex align-items-center">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={currentPage === 1 || isPageChanging}
                            onClick={() => handlePageChange(1)}
                            className="me-2"
                          >
                            First
                          </Button>

                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={currentPage === 1 || isPageChanging}
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="me-2"
                          >
                            Previous
                          </Button>

                          <div className="d-flex align-items-center mx-2">
                            <span className="me-2">Page</span>
                            <input
                              type="number"
                              min="1"
                              max={totalPages}
                              value={currentPage}
                              onChange={(e) => {
                                const page = parseInt(e.target.value);
                                if (page >= 1 && page <= totalPages) {
                                  handlePageChange(page);
                                }
                              }}
                              className="form-control form-control-sm"
                              style={{ width: '70px' }}
                              disabled={isPageChanging}
                            />
                            <span className="ms-2">of {totalPages}</span>
                          </div>

                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={currentPage === totalPages || isPageChanging}
                            onClick={() => handlePageChange(currentPage + 1)}
                            className="me-2"
                          >
                            Next
                          </Button>

                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={currentPage === totalPages || isPageChanging}
                            onClick={() => handlePageChange(totalPages)}
                          >
                            Last
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </Col>
          </Row>
        </Container>

        {/* Add Material Modal */}
        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>ADD NEW RAW MATERIAL</Modal.Title>
          </Modal.Header>
          <Form onSubmit={addMaterial}>
            <Modal.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId="formMainCategory" className="mb-3">
                    <Form.Label className='fw-semibold'>Main Category <span className="text-danger">*</span></Form.Label>
                    <Select
                      options={mainCategoryOptions}
                      value={mainCategoryOptions.find(opt => opt.value === formData.mainCategory)}
                      onChange={(selected) => handleSelectChange(selected, 'mainCategory')}
                      isClearable
                      isSearchable
                      placeholder="Select main category"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formSubCategory" className="mb-3">
                    <Form.Label className='fw-semibold'>Sub Category</Form.Label>
                    <Select
                      options={subCategoryOptions}
                      value={subCategoryOptions.find(opt => opt.value === formData.subCategory)}
                      onChange={(selected) => handleSelectChange(selected, 'subCategory')}
                      isClearable
                      isSearchable
                      placeholder="Select sub category"
                      isDisabled={!formData.mainCategory}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId="formName" className="mb-3">
                    <Form.Label className='fw-semibold'>Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      placeholder="Enter material name"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formUnit" className="mb-3">
                    <Form.Label className='fw-semibold'>Unit <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="unit"
                      value={formData.unit}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select unit</option>
                      <option value="LITRES">LITRES</option>
                      <option value="ML">ML</option>
                      <option value="GRAMS">GRAMS</option>
                      <option value="KILOGRAMS">KILOGRAMS</option>
                      <option value="PACKETS">PACKETS</option>
                      <option value="PCS">PCS</option>
                      <option value="BOX">BOX</option>
                      <option value="NOS">NOS</option>
                      <option value="MM">MM</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId="formHSN" className="mb-3">
                    <Form.Label className='fw-semibold'>HSN Code <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="hsn"
                      value={formData.hsn}
                      onChange={handleFormChange}
                      placeholder="Enter HSN code"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formTax" className="mb-3">
                    <Form.Label className='fw-semibold'>Tax Percentage <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="number"
                      name="taxPercent"
                      value={formData.taxPercent}
                      onChange={handleFormChange}
                      min="0"
                      max="100"
                      placeholder="Enter tax percentage"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <Form.Group controlId="formUnit" className="mb-3">
                    <Form.Label className='fw-semibold'>Purchase Unit <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="purchaseUnit"
                      value={formData.purchaseUnit}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select purchase unit</option>
                      <option value="FOOD">Food</option>
                      <option value="BEVERAGE">Beverage</option>
                      <option value="CENTRAL">Central</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group controlId="formReorder" className="mb-3">
                    <Form.Label className='fw-semibold'>Re-Order Level</Form.Label>
                    <Form.Control
                      type="number"
                      name="reOrderLevel"
                      value={formData.reOrderLevel}
                      onChange={handleFormChange}
                      min="0"
                      placeholder="Enter re-order level"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group controlId="formDescription" className="mb-3">
                    <Form.Label className='fw-semibold'>Description <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      placeholder="Enter description"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button variant="warning" type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    <span className="ms-2">Adding...</span>
                  </>
                ) : (
                  "Add Material"
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* View Material Modal */}
        <Modal show={showViewModal} onHide={() => setShowViewModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Material Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Table striped bordered>
              <tbody>
                <tr>
                  <td><strong>Name</strong></td>
                  <td>{editFormData.name}</td>
                </tr>
                <tr>
                  <td><strong>Main Category</strong></td>
                  <td>{mainCategories.find(cat => cat._id === editFormData.mainCategory)?.name || '-'}</td>
                </tr>
                <tr>
                  <td><strong>Sub Category</strong></td>
                  <td>{subCategories.find(cat => cat._id === editFormData.subCategory)?.name || '-'}</td>
                </tr>
                <tr>
                  <td><strong>Description</strong></td>
                  <td>{editFormData.description || '-'}</td>
                </tr>
                <tr>
                  <td><strong>HSN Code</strong></td>
                  <td>{editFormData.hsn || '-'}</td>
                </tr>
                <tr>
                  <td><strong>Tax Percentage</strong></td>
                  <td>{editFormData.taxPercent ? `${editFormData.taxPercent}%` : '0%'}</td>
                </tr>
                <tr>
                  <td><strong>Unit</strong></td>
                  <td>{editFormData.unit || '-'}</td>
                </tr>
                <tr>
                  <td><strong>Re-Order Level</strong></td>
                  <td>{editFormData.reOrderLevel || '-'}</td>
                </tr>
              </tbody>
            </Table>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowViewModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Edit Material Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>EDIT RAW MATERIAL</Modal.Title>
          </Modal.Header>
          <Form onSubmit={updateMaterial}>
            <Modal.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId="editMainCategory" className="mb-3">
                    <Form.Label className='fw-semibold'>Main Category <span className="text-danger">*</span></Form.Label>
                    <Select
                      options={mainCategoryOptions}
                      value={mainCategoryOptions.find(opt => opt.value === editFormData.mainCategory)}
                      onChange={(selected) => handleEditSelectChange(selected, 'mainCategory')}
                      isClearable
                      isSearchable
                      placeholder="Select main category"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="editSubCategory" className="mb-3">
                    <Form.Label className='fw-semibold'>Sub Category</Form.Label>
                    <Select
                      options={subCategoryOptions}
                      value={subCategoryOptions.find(opt => opt.value === editFormData.subCategory)}
                      onChange={(selected) => handleEditSelectChange(selected, 'subCategory')}
                      isClearable
                      isSearchable
                      placeholder="Select sub category"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId="editName" className="mb-3">
                    <Form.Label className='fw-semibold'>Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={editFormData.name}
                      onChange={handleEditFormChange}
                      placeholder="Enter material name"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="editUnit" className="mb-3">
                    <Form.Label className='fw-semibold'>Unit <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="unit"
                      value={editFormData.unit}
                      onChange={handleEditFormChange}
                      required
                    >
                      <option value="">Select unit</option>
                      <option value="LITRES">LITRES</option>
                      <option value="ML">ML</option>
                      <option value="GRAMS">GRAMS</option>
                      <option value="KILOGRAMS">KILOGRAMS</option>
                      <option value="PACKETS">PACKETS</option>
                      <option value="PCS">PCS</option>
                      <option value="BOX">BOX</option>
                      <option value="NOS">NOS</option>
                      <option value="MM">MM</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId="editHSN" className="mb-3">
                    <Form.Label className='fw-semibold'>HSN Code <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="hsn"
                      value={editFormData.hsn}
                      onChange={handleEditFormChange}
                      placeholder="Enter HSN code"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="editTax" className="mb-3">
                    <Form.Label className='fw-semibold'>Tax Percentage <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="number"
                      name="taxPercent"
                      value={editFormData.taxPercent}
                      onChange={handleEditFormChange}
                      min="0"
                      max="100"
                      placeholder="Enter tax percentage"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <Form.Group controlId="formUnit" className="mb-3">
                    <Form.Label className='fw-semibold'>Purchase Unit <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="purchaseUnit"
                      value={editFormData.purchaseUnit}
                      onChange={handleEditFormChange}
                      required
                    >
                      <option value="">Select purchase unit</option>
                      <option value="FOOD">Food</option>
                      <option value="BEVERAGE">Beverage</option>
                      <option value="CENTRAL">Central</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group controlId="editReorder" className="mb-3">
                    <Form.Label className='fw-semibold'>Re-Order Level</Form.Label>
                    <Form.Control
                      type="number"
                      name="reOrderLevel"
                      value={editFormData.reOrderLevel}
                      onChange={handleEditFormChange}
                      min="0"
                      placeholder="Enter re-order level"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group controlId="editDescription" className="mb-3">
                    <Form.Label className='fw-semibold'>Description <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="description"
                      value={editFormData.description}
                      onChange={handleEditFormChange}
                      placeholder="Enter description"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button variant="warning" type="submit" disabled={isEditLoading}>
                {isEditLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    <span className="ms-2">Saving...</span>
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Deletion</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete <strong>{editFormData.name}</strong>? This action cannot be undone.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={deleteMaterial} disabled={isDeleteLoading}>
              {isDeleteLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  <span className="ms-2">Deleting...</span>
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default RawMaterials;